import { getChatExportFormat } from '@/pocs/chat/export/formats'
import { SECTIONS } from '@/types/sections'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useApi } from '@/composables/api'
import { useNodeApi } from '@/composables/node/node-api'
import { useNodeUploader } from '@/components/upload/composables'
import type { ChatExportFormat, ChatTranscript } from '@/pocs/chat/export/types'
import type { Node, PathSegment } from '@deepcloud/api-deep-box'

interface ChatExportResult {
  node: Node
  // set when the intermediate DOCX of a PDF export could not be removed
  leftoverFileName?: string
}

interface ChatExportRequest {
  transcript: ChatTranscript
  format: ChatExportFormat
  fileName: string
  typeId: string
  boxId: string
  folder: PathSegment
}

export function useDeepAChatExport() {
  const { deepBoxAPI } = useApi()
  const { uploadFile } = useNodeUploader()
  const nodeApi = useNodeApi()
  const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()

  // mirrors the app's permanent delete: purge is only applied to trashed nodes
  async function removeTemporaryNode(nodeId: string) {
    try {
      await deepBoxAPI.value.nodes.deleteById(nodeId)
      await deepBoxAPI.value.nodes.deleteById(nodeId, { purge: true })
      return true
    } catch {
      return false
    }
  }

  async function convertToPdf(docxNode: Node): Promise<ChatExportResult> {
    const pdfNode = await deepBoxAPI.value.nodesConvert
      .create(docxNode.nodeId)
      .then(({ data }) => data)
      .catch(async (error: unknown) => {
        await removeTemporaryNode(docxNode.nodeId)
        throw error
      })
    const isRemoved = await removeTemporaryNode(docxNode.nodeId)
    return isRemoved
      ? { node: pdfNode }
      : { node: pdfNode, leftoverFileName: docxNode.displayName }
  }

  function showInCurrentFolder(node: Node, folder: PathSegment) {
    if (
      deepBoxDeepBoxesBoxesNodesStore.getCurrentRootNodeId === folder.nodeId
    ) {
      nodeApi.addFileFulfilled({ node, section: SECTIONS.FILES })
    }
  }

  async function exportChat({
    transcript,
    format,
    fileName,
    typeId,
    boxId,
    folder,
  }: ChatExportRequest): Promise<ChatExportResult> {
    const definition = getChatExportFormat(format)
    const { blob, extension } = await definition.render(transcript)
    const uploadedNode = await uploadFile({
      section: SECTIONS.FILES,
      parentNodeId: folder.nodeId,
      file: new File([blob], `${fileName}.${extension}`, { type: blob.type }),
      typeId,
      boxId,
    })
    const result = definition.convertToPdf
      ? await convertToPdf(uploadedNode)
      : { node: uploadedNode }
    showInCurrentFolder(result.node, folder)
    return result
  }

  return { exportChat }
}
